import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from 'i18n';

import { getRoutes } from 'shared/utils/runtimeBasePath';
import { getUrlParameter, isFalsy } from 'shared/utils/common';
import { normalizeSupportedLanguage } from 'shared/utils/supportedLanguage';
import { useUserSessionContext } from 'features/session/userSessionStore';
import { useHostAuth, isEmbedded, isHostAuthenticated } from 'features/session/VirtualAuthContext';
import { AUTH_EVENTS } from 'features/session/auth-events';
import { ticketsQueryKeys } from 'features/tickets/queries';
import { clearActiveVirtualSessionId, setActiveVirtualSessionId } from 'shared/api/http-client';

import {
  createVirtualSessionId,
  fetchGuestConfig,
  fetchVirtualSession,
  validatePlatformSession,
  VIRTUAL_NOT_ALLOWED_CODE,
  VirtualNotAllowedError
} from './api';
import type { SessionUser } from './types';

function isVirtualNotAllowedError(e: unknown): e is VirtualNotAllowedError {
  return e instanceof VirtualNotAllowedError || (e != null && typeof e === 'object' && (e as { code?: string }).code === VIRTUAL_NOT_ALLOWED_CODE);
}

function isSessionInvalidError(e: unknown): boolean {
  if (e == null || typeof e !== 'object') return false;
  const err = e as { response?: { status?: number } };
  return err.response?.status === 401 || err.response?.status === 404;
}

function getGuestParams() {
  const language = String(getUrlParameter('language') || 'EN');
  const timezone = String(getUrlParameter('timezone') || 'UTC');
  const currency = String(getUrlParameter('currency') || 'USD');
  const odds_format = String(getUrlParameter('odds_format') || 'decimal');
  const domain = String(getUrlParameter('domain') || '');
  return { language, timezone, currency, odds_format, domain: domain || undefined };
}

function isValidSessionId(raw: unknown): raw is string {
  if (raw === false || raw === null || raw === undefined) return false;
  if (typeof raw !== 'string') return false;
  const s = raw.trim();
  return s !== '' && s !== 'false' && s !== 'null';
}

async function bootstrapFromPlatformSessionId(
  platformSessionId: string
): Promise<SessionUser> {
  const userData = await validatePlatformSession({ sessionId: platformSessionId });
  const virtualSessionId = await createVirtualSessionId({ platformSessionId, userData });

  setActiveVirtualSessionId(virtualSessionId);

  const session = await fetchVirtualSession({ virtualSessionId });
  return session as SessionUser;
}

export function useBootstrapSession() {
  const [{ user }, dispatch] = useUserSessionContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const hostAuth = useHostAuth();
  const embedded = isEmbedded(hostAuth);
  const embeddedWithAuth = isHostAuthenticated(hostAuth);

  // Embedded: session from host props only (single source; no LocalStorage/cookie fallback).
  // Standalone: session from ?s= URL param only.
  let platformSessionId: string | null;
  if (embeddedWithAuth) {
    platformSessionId = hostAuth.platformSessionId ?? null;
  } else if (embedded) {
    platformSessionId = null;
  } else {
    platformSessionId = isValidSessionId(getUrlParameter('s')) ? (getUrlParameter('s') as string) : null;
  }

  const query = useQuery({
    queryKey: ['session', 'bootstrap', platformSessionId ?? 'guest'],
    queryFn: async (): Promise<SessionUser> => {
      if (platformSessionId) {
        try {
          return await bootstrapFromPlatformSessionId(platformSessionId);
        } catch (err) {
          if (!embedded && isSessionInvalidError(err)) {
            clearActiveVirtualSessionId();
            return fetchGuestConfig(getGuestParams());
          }
          if (isVirtualNotAllowedError(err)) throw err;
          throw err;
        }
      }

      clearActiveVirtualSessionId();
      return fetchGuestConfig(getGuestParams());
    },
    retry: (failureCount, error) => {
      if (isVirtualNotAllowedError(error)) return false;
      if (isSessionInvalidError(error)) return false;
      return failureCount < 3;
    },
  });

  // Apply session data to context + i18n when query resolves
  const applySession = React.useCallback((session: SessionUser) => {
    if (i18n && session.language) {
      const sessionLang = normalizeSupportedLanguage(session.language);
      if (sessionLang !== i18n.language) {
        i18n.changeLanguage(sessionLang);
      }
    }

    if (!session.session_id || session.guest) {
      clearActiveVirtualSessionId();
      queryClient.removeQueries({ queryKey: ticketsQueryKeys.creditAvailable });
    } else {
      queryClient.invalidateQueries({ queryKey: ticketsQueryKeys.creditAvailable });
    }
    dispatch({ type: 'SET_USER', payload: session });

    const routes = getRoutes();
    if (!isFalsy(session) && location.pathname === routes.NO_SESSION) {
      navigate(routes.HOME, { replace: true });
    }
  }, [dispatch, location.pathname, navigate, queryClient]);

  React.useEffect(() => {
    if (!query.data) return;
    const isGuestData = !!(query.data as SessionUser).guest;
    if (embedded && isGuestData && platformSessionId !== null) return;
    if (embedded && !isGuestData && platformSessionId === null) return;
    if (JSON.stringify(user) !== JSON.stringify(query.data)) {
      applySession(query.data);
    }
  }, [applySession, embedded, platformSessionId, query.data, user]);

  React.useEffect(() => {
    if (!embedded || platformSessionId != null) return;
    clearActiveVirtualSessionId();
  }, [embedded, platformSessionId]);

  // When embedded and bootstrap fails with 401/404 (session invalid), notify host so it refreshes auth and clears session
  React.useEffect(() => {
    if (!embedded || !query.isError || !query.error) return;
    if (!isSessionInvalidError(query.error)) return;
    window.dispatchEvent(new CustomEvent(AUTH_EVENTS.SESSION_INVALID, { detail: { reason: 'validate_failed' } }));
  }, [embedded, query.error, query.isError]);

  // Redirect to no-session page when virtual sports is not allowed
  React.useEffect(() => {
    if (!query.isError || !query.error) return;
    const code = (query.error as { code?: string })?.code;
    if (code !== VIRTUAL_NOT_ALLOWED_CODE) return;
    const routes = getRoutes();
    if (location.pathname === routes.NO_SESSION) return;
    navigate(routes.NO_SESSION, { replace: true });
  }, [location.pathname, navigate, query.error, query.isError]);

  React.useEffect(() => {
    const handler = () => {
      clearActiveVirtualSessionId();
      if (!embedded) {
        queryClient.invalidateQueries({ queryKey: ['session', 'bootstrap'] });
      }
    };
    window.addEventListener(AUTH_EVENTS.SESSION_INVALID, handler);
    return () => window.removeEventListener(AUTH_EVENTS.SESSION_INVALID, handler);
  }, [embedded, queryClient]);

  return query;
}
